import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "CobrosSuscripciones";

const RUTAS_FRONTEND = {
  inicio: "/inicio",
  nuevo: "/cobros/planes/nuevo",
  editar: (id) => `/cobros/planes/edit/${id}`,
};

/* =========================================================
   UTILIDADES GENERALES
========================================================= */

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const leerStorageJSON = (clave) => {
  try {
    const valor = localStorage.getItem(clave);
    return valor ? JSON.parse(valor) : null;
  } catch (error) {
    console.error(
      `No se pudo leer ${clave}:`,
      error
    );

    return null;
  }
};

const obtenerUsuarioStorage = () =>
  leerStorageJSON("usuario") ||
  leerStorageJSON("user") ||
  leerStorageJSON("usuarios") ||
  {};

const obtenerModulosStorage = () => {
  const permisos =
    leerStorageJSON("permisos");

  const usuario =
    obtenerUsuarioStorage();

  const modulos = Array.isArray(permisos)
    ? permisos
    : permisos?.modulos ||
      permisos?.permisos ||
      usuario?.modulos ||
      usuario?.permisos ||
      usuario?.usuario?.modulos ||
      usuario?.usuario?.permisos ||
      usuario?.user?.modulos ||
      usuario?.user?.permisos ||
      [];

  return Array.isArray(modulos)
    ? modulos
    : [];
};

/* =========================================================
   SUPERADMIN / ADMINISTRADOR

   Se mantiene únicamente dentro de la resolución de permisos.
   No se usa como condición adicional en los botones.
========================================================= */

const esSuperAdmin = () => {
  const usuario =
    obtenerUsuarioStorage();

  const rol =
    usuario?.rolObjectId?.nombreRol ||
    usuario?.rol?.nombreRol ||
    usuario?.rol?.nombre ||
    usuario?.rol ||
    usuario?.role ||
    usuario?.tipoUsuario ||
    usuario?.nombreRol ||
    usuario?.usuario?.rolObjectId
      ?.nombreRol ||
    usuario?.usuario?.rol?.nombreRol ||
    usuario?.usuario?.rol?.nombre ||
    usuario?.usuario?.rol ||
    usuario?.user?.rolObjectId
      ?.nombreRol ||
    usuario?.user?.rol?.nombreRol ||
    usuario?.user?.rol?.nombre ||
    usuario?.user?.rol ||
    "";

  const rolNormalizado =
    normalizarTexto(rol);

  return (
    rolNormalizado.includes(
      "superadmin"
    ) ||
    rolNormalizado.includes(
      "administrador"
    ) ||
    rolNormalizado === "admin"
  );
};

/* =========================================================
   BUSCAR MÓDULO EN PERMISOS
========================================================= */

const obtenerModuloPorNombre = (
  nombreModulo
) => {
  const moduloBuscado =
    normalizarTexto(nombreModulo);

  return (
    obtenerModulosStorage().find(
      (modulo) => {
        const nombre =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.moduloNombre ||
          modulo?.nombre ||
          modulo?.name ||
          modulo?.moduloObjectId
            ?.nombreModulo ||
          modulo?.moduloObjectId
            ?.nombre ||
          "";

        return (
          normalizarTexto(nombre) ===
          moduloBuscado
        );
      }
    ) || {}
  );
};

/* =========================================================
   VALIDAR PERMISO

   Soporta distintas formas en que MrGestor puede guardar
   los permisos en localStorage.
========================================================= */

const tienePermiso = (
  modulo,
  accion
) => {
  if (esSuperAdmin()) {
    return true;
  }

  const accionNormalizada =
    normalizarTexto(accion);

  const camposPorAccion = {
    leer: [
      "puedeLeer",
      "leer",
      "read",
      "ver",
      "listar",
    ],

    crear: [
      "puedeCrear",
      "crear",
      "create",
      "add",
      "registrar",
    ],

    actualizar: [
      "puedeActualizar",
      "actualizar",
      "update",
      "editar",
      "edit",
    ],

    eliminar: [
      "puedeEliminar",
      "eliminar",
      "delete",
      "borrar",
      "remove",
    ],
  };

  const campos =
    camposPorAccion[
      accionNormalizada
    ] || [accion];

  const permisoDirecto =
    campos.some(
      (campo) =>
        modulo?.[campo] === true ||
        modulo?.acciones?.[campo] ===
          true ||
        modulo?.permisos?.[campo] ===
          true
    );

  const permisosEnArreglo = [
    ...(Array.isArray(
      modulo?.acciones
    )
      ? modulo.acciones
      : []),

    ...(Array.isArray(
      modulo?.permisos
    )
      ? modulo.permisos
      : []),

    ...(Array.isArray(
      modulo?.operaciones
    )
      ? modulo.operaciones
      : []),
  ].map(normalizarTexto);

  return (
    permisoDirecto ||
    campos.some((campo) =>
      permisosEnArreglo.includes(
        normalizarTexto(campo)
      )
    )
  );
};

/* =========================================================
   FORMATO DE MONEDA
========================================================= */

const formatearMoneda = (
  valor,
  moneda = "COP"
) => {
  const numero =
    Number(valor || 0);

  try {
    return new Intl.NumberFormat(
      "es-CO",
      {
        style: "currency",
        currency:
          moneda || "COP",
        maximumFractionDigits: 0,
      }
    ).format(numero);
  } catch {
    return `$ ${numero.toLocaleString(
      "es-CO"
    )}`;
  }
};

/* =========================================================
   FORMATO DE DURACIÓN
========================================================= */

const obtenerDuracion = (
  plan = {}
) => {
  const cantidad =
    Number(
      plan.cantidadDuracion || 0
    );

  switch (
    String(
      plan.tipoDuracion || ""
    ).toUpperCase()
  ) {
    case "DIAS":
      return `${cantidad} día(s)`;

    case "MESES":
      return `${cantidad} mes(es)`;

    case "ANIOS":
      return `${cantidad} año(s)`;

    case "PERSONALIZADO":
      return cantidad > 0
        ? `${cantidad} personalizado`
        : "Personalizado";

    default:
      return "—";
  }
};

/* =========================================================
   BADGE DE ESTADO
========================================================= */

const badgeEstado = (
  estado = ""
) => {
  const valor =
    String(estado || "")
      .toUpperCase();

  const clases = {
    ACTIVO: "text-bg-success",
    ACTIVA: "text-bg-success",
    APROBADO: "text-bg-success",
    APROBADA: "text-bg-success",

    PENDIENTE:
      "text-bg-warning",
    POR_VENCER:
      "text-bg-warning",
    EN_REVISION:
      "text-bg-info",

    GRACIA: "text-bg-info",

    VENCIDO: "text-bg-danger",
    VENCIDA: "text-bg-danger",
    RECHAZADO: "text-bg-danger",
    RECHAZADA: "text-bg-danger",
    SUSPENDIDA:
      "text-bg-danger",

    CANCELADA:
      "text-bg-secondary",
    ANULADO:
      "text-bg-secondary",
    REEMBOLSADO:
      "text-bg-secondary",
    INACTIVO:
      "text-bg-secondary",
  };

  const clase =
    clases[valor] ||
    "text-bg-secondary";

  return (
    <span
      className={`badge rounded-pill ${clase}`}
    >
      {valor.replaceAll("_", " ") ||
        "—"}
    </span>
  );
};

/* =========================================================
   COMPONENTE
========================================================= */

const ListarPlanes = () => {
  const navigate =
    useNavigate();

  const [planes, setPlanes] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [
    procesandoId,
    setProcesandoId,
  ] = useState(null);

  /* =======================================================
     AUTENTICACIÓN
  ======================================================= */

  const authConfig =
    useCallback(() => {
      const token =
        localStorage.getItem(
          "token"
        ) ||
        sessionStorage.getItem(
          "token"
        );

      return {
        headers: {
          Authorization:
            `Bearer ${token}`,
        },
      };
    }, []);

  /* =======================================================
     PERMISOS
  ======================================================= */

  const moduloCobros =
    useMemo(
      () =>
        obtenerModuloPorNombre(
          MODULO
        ),
      []
    );

  const puedeLeer =
    useMemo(
      () =>
        tienePermiso(
          moduloCobros,
          "LEER"
        ),
      [moduloCobros]
    );

  const puedeCrear =
    useMemo(
      () =>
        tienePermiso(
          moduloCobros,
          "CREAR"
        ),
      [moduloCobros]
    );

  const puedeActualizar =
    useMemo(
      () =>
        tienePermiso(
          moduloCobros,
          "ACTUALIZAR"
        ),
      [moduloCobros]
    );

  const puedeEliminar =
    useMemo(
      () =>
        tienePermiso(
          moduloCobros,
          "ELIMINAR"
        ),
      [moduloCobros]
    );

  /* =======================================================
     SESIÓN EXPIRADA
  ======================================================= */

  const manejarErrorSesion =
    useCallback(
      (error) => {
        if (
          error?.response
            ?.status === 401
        ) {
          Swal.fire({
            icon: "warning",
            title:
              "Sesión expirada",
            text:
              "Por favor inicia sesión nuevamente.",
            confirmButtonText:
              "Aceptar",
          }).then(() => {
            localStorage.removeItem(
              "token"
            );

            sessionStorage.removeItem(
              "token"
            );

            navigate("/", {
              replace: true,
            });
          });

          return true;
        }

        return false;
      },
      [navigate]
    );

  /* =======================================================
     LISTAR PLANES
  ======================================================= */

  const getData =
    useCallback(async () => {
      if (!puedeLeer) {
        setPlanes([]);
        return;
      }

      try {
        setLoading(true);

        const { data } =
          await axios.get(
            "/api/planes/listall",
            authConfig()
          );

        if (
          !data ||
          !Array.isArray(
            data.planes
          )
        ) {
          throw new Error(
            "Datos de planes no válidos."
          );
        }

        setPlanes(
          data.planes
        );
      } catch (error) {
        console.error(
          "Error al cargar planes:",
          error
        );

        if (
          manejarErrorSesion(
            error
          )
        ) {
          return;
        }

        Swal.fire(
          "Error",
          error.response?.data
            ?.message ||
            "No se pudieron cargar los planes.",
          "error"
        );

        setPlanes([]);
      } finally {
        setLoading(false);
      }
    }, [
      authConfig,
      manejarErrorSesion,
      puedeLeer,
    ]);

  useEffect(() => {
    getData();
  }, [getData]);

  /* =======================================================
     CREAR PLAN
  ======================================================= */

  const nuevoPlan =
    useCallback(() => {
      if (!puedeCrear) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para crear planes.",
          "warning"
        );

        return;
      }

      navigate(
        RUTAS_FRONTEND.nuevo
      );
    }, [
      navigate,
      puedeCrear,
    ]);

  /* =======================================================
     EDITAR PLAN
  ======================================================= */

  const editarPlan =
    useCallback(
      (plan) => {
        if (
          !puedeActualizar
        ) {
          Swal.fire(
            "Sin permisos",
            "No tiene permisos para actualizar planes.",
            "warning"
          );

          return;
        }

        navigate(
          RUTAS_FRONTEND.editar(
            plan._id
          )
        );
      },
      [
        navigate,
        puedeActualizar,
      ]
    );

  /* =======================================================
     CAMBIAR ESTADO
  ======================================================= */

  const cambiarEstado =
    useCallback(
      async (plan) => {
        if (
          !puedeActualizar
        ) {
          Swal.fire(
            "Sin permisos",
            "No tiene permisos para cambiar el estado de los planes.",
            "warning"
          );

          return;
        }

        const nuevoEstado =
          !plan.estadoPlan;

        const result =
          await Swal.fire({
            title: nuevoEstado
              ? "¿Activar plan?"
              : "¿Desactivar plan?",

            text: nuevoEstado
              ? "El plan volverá a estar disponible."
              : "El plan dejará de estar disponible para nuevas suscripciones.",

            icon: "question",
            showCancelButton: true,

            confirmButtonText:
              nuevoEstado
                ? "Sí, activar"
                : "Sí, desactivar",

            cancelButtonText:
              "Cancelar",
          });

        if (
          !result.isConfirmed
        ) {
          return;
        }

        try {
          setProcesandoId(
            plan._id
          );

          const { data } =
            await axios.put(
              `/api/planes/cambiarestado/${plan._id}`,
              {
                estadoPlan:
                  nuevoEstado,
              },
              authConfig()
            );

          await Swal.fire({
            icon: "success",

            title:
              data?.message ||
              "Estado actualizado",

            showConfirmButton:
              false,

            timer: 1500,
          });

          await getData();
        } catch (error) {
          console.error(
            "Error al cambiar estado del plan:",
            error
          );

          if (
            manejarErrorSesion(
              error
            )
          ) {
            return;
          }

          Swal.fire(
            "Error",
            error.response?.data
              ?.message ||
              "No se pudo cambiar el estado.",
            "error"
          );
        } finally {
          setProcesandoId(
            null
          );
        }
      },
      [
        authConfig,
        getData,
        manejarErrorSesion,
        puedeActualizar,
      ]
    );

  /* =======================================================
     ELIMINAR PLAN
  ======================================================= */

  const eliminarPlan =
    useCallback(
      async (plan) => {
        if (
          !puedeEliminar
        ) {
          Swal.fire(
            "Sin permisos",
            "No tiene permisos para eliminar planes.",
            "warning"
          );

          return;
        }

        const result =
          await Swal.fire({
            title:
              "¿Eliminar plan?",

            text:
              "Si el plan tiene suscripciones asociadas, el sistema debe conservarlo o desactivarlo para proteger el historial.",

            icon: "warning",
            showCancelButton: true,

            confirmButtonText:
              "Sí, continuar",

            cancelButtonText:
              "Cancelar",

            confirmButtonColor:
              "#dc3545",
          });

        if (
          !result.isConfirmed
        ) {
          return;
        }

        try {
          setProcesandoId(
            plan._id
          );

          const { data } =
            await axios.delete(
              `/api/planes/delete/${plan._id}`,
              authConfig()
            );

          await Swal.fire({
            icon: "success",

            title:
              data?.message ||
              "Proceso realizado correctamente",

            showConfirmButton:
              false,

            timer: 1800,
          });

          await getData();
        } catch (error) {
          console.error(
            "Error al eliminar plan:",
            error
          );

          if (
            manejarErrorSesion(
              error
            )
          ) {
            return;
          }

          Swal.fire(
            "Error",
            error.response?.data
              ?.message ||
              "No se pudo eliminar el plan.",
            "error"
          );
        } finally {
          setProcesandoId(
            null
          );
        }
      },
      [
        authConfig,
        getData,
        manejarErrorSesion,
        puedeEliminar,
      ]
    );

  /* =======================================================
     DATOS PARA DATATABLE
  ======================================================= */

  const dataTabla =
    useMemo(
      () =>
        planes.map(
          (plan) => ({
            ...plan,

            valorFormateado:
              formatearMoneda(
                plan.valorPlan,
                plan.moneda
              ),

            duracion:
              obtenerDuracion(
                plan
              ),

            gracia:
              `${
                Number(
                  plan.diasGracia ||
                    0
                )
              } día(s)`,

            renovable:
              plan.permiteRenovacion
                ? "Sí"
                : "No",

            estado:
              plan.estadoPlan
                ? badgeEstado(
                    "ACTIVO"
                  )
                : badgeEstado(
                    "INACTIVO"
                  ),

            tipo:
              plan.esPrueba
                ? "Prueba"
                : plan.esCortesia
                  ? "Cortesía"
                  : "Comercial",

            funciones: (
              <div className="d-flex gap-2 flex-wrap justify-content-end">
                {/* EDITAR */}
                {puedeActualizar && (
                  <button
                    className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 34,
                      height: 34,
                    }}
                    onClick={() =>
                      editarPlan(
                        plan
                      )
                    }
                    title="Editar plan"
                    type="button"
                    disabled={
                      loading ||
                      procesandoId ===
                        plan._id
                    }
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </button>
                )}

                {/* ACTIVAR / DESACTIVAR */}
                {puedeActualizar && (
                  <button
                    className={`btn ${
                      plan.estadoPlan
                        ? "btn-outline-secondary"
                        : "btn-success"
                    } btn-sm d-inline-flex align-items-center justify-content-center`}
                    style={{
                      width: 34,
                      height: 34,
                    }}
                    onClick={() =>
                      cambiarEstado(
                        plan
                      )
                    }
                    title={
                      plan.estadoPlan
                        ? "Desactivar plan"
                        : "Activar plan"
                    }
                    type="button"
                    disabled={
                      loading ||
                      procesandoId ===
                        plan._id
                    }
                  >
                    {procesandoId ===
                    plan._id ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <i
                        className={`fa-solid ${
                          plan.estadoPlan
                            ? "fa-toggle-off"
                            : "fa-toggle-on"
                        }`}
                      ></i>
                    )}
                  </button>
                )}

                {/* ELIMINAR */}
                {puedeEliminar && (
                  <button
                    className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 34,
                      height: 34,
                    }}
                    onClick={() =>
                      eliminarPlan(
                        plan
                      )
                    }
                    title="Eliminar plan"
                    type="button"
                    disabled={
                      loading ||
                      procesandoId ===
                        plan._id
                    }
                  >
                    {procesandoId ===
                    plan._id ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa-solid fa-trash"></i>
                    )}
                  </button>
                )}
              </div>
            ),
          })
        ),
      [
        planes,
        loading,
        procesandoId,
        puedeActualizar,
        puedeEliminar,
        editarPlan,
        cambiarEstado,
        eliminarPlan,
      ]
    );

  /* =======================================================
     COLUMNAS
  ======================================================= */

  const columns =
    useMemo(
      () => [
        {
          Header: "Código",
          accessor: "codigoPlan",
        },
        {
          Header: "Plan",
          accessor: "nombrePlan",
        },
        {
          Header: "Duración",
          accessor: "duracion",
        },
        {
          Header: "Valor",
          accessor:
            "valorFormateado",
        },
        {
          Header: "Gracia",
          accessor: "gracia",
        },
        {
          Header: "Renovable",
          accessor: "renovable",
          hideHeaderOnMobile:
            true,
        },
        {
          Header: "Tipo",
          accessor: "tipo",
          hideHeaderOnMobile:
            true,
        },
        {
          Header: "Estado",
          accessor: "estado",
        },
        {
          Header: "Acciones",
          accessor: "funciones",
        },
      ],
      []
    );

  /* =======================================================
     SIN PERMISO PARA LEER
  ======================================================= */

  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-4">
          <div className="alert alert-warning mb-3">
            <i className="fa-solid fa-triangle-exclamation me-2"></i>

            No tiene permisos para consultar los planes.
          </div>

          <button
            type="button"
            className="btn btn-dark"
            onClick={() =>
              navigate(
                RUTAS_FRONTEND.inicio
              )
            }
          >
            <i className="fa-solid fa-arrow-left me-2"></i>

            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     VISTA
  ======================================================= */

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* ===============================================
              ENCABEZADO
          =============================================== */}

          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Planes
                </h5>

                <small className="text-secondary">
                  Gestión de planes, duración, valores y disponibilidad.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() =>
                    navigate(
                      RUTAS_FRONTEND.inicio
                    )
                  }
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {/* El botón depende solamente del permiso CREAR */}
                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={
                      nuevoPlan
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>

                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ===============================================
              CONTENIDO
          =============================================== */}

          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando planes…
                </div>
              </div>
            ) : dataTabla.length >
              0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={columns}
                  data={dataTabla}
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5 px-3">
                <div className="display-6 mb-2">
                  💳
                </div>

                <div className="fw-semibold">
                  No hay planes registrados
                </div>

                <div className="small mb-3">
                  Crea el primer plan para iniciar la gestión de suscripciones.
                </div>

                {puedeCrear ? (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={
                      nuevoPlan
                    }
                  >
                    <i className="fa-solid fa-plus me-2"></i>

                    Crear plan
                  </button>
                ) : (
                  <div className="alert alert-warning d-inline-block mt-2 mb-0">
                    <i className="fa-solid fa-lock me-2"></i>

                    Tiene permiso para consultar, pero no para crear planes.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarPlanes;